/**
 * BFF (Backend For Frontend) API endpoints.
 * All endpoints available through the BFF gateway.
 * The `{version}` placeholder is interpolated at runtime with the desired API version.
 */
export const BFF_ENDPOINTS = {
  // User/Identity endpoints
  VALIDATE_EMAIL: '{version}/identities/registrations',

  // Subscription endpoints
  SUBSCRIPTION: '{version}/subscriptions/{subscriptionId}',
  SUBSCRIPTION_FEATURES: '{version}/subscriptions/{subscriptionId}/features',
  CANCEL_SUBSCRIPTION: '{version}/subscriptions/{subscriptionId}/cancellations',
  SUBSCRIPTION_CANCELLATION: '{version}/subscriptions/{subscriptionId}/cancellations/{cancellationId}',
  SUBSCRIPTION_BENEFICIARIES: '{version}/subscriptions/{subscriptionId}/beneficiaries',
  SUBSCRIPTION_BENEFICIARY: '{version}/subscriptions/{subscriptionId}/beneficiaries/{beneficiaryId}',
  SUBSCRIPTION_PASSENGERS: '{version}/subscriptions/{subscriptionId}/passengers',
  SUBSCRIPTION_PASSENGERS_EVALUATION: '{version}/subscriptions/{subscriptionId}/passengers/evaluation',

  // Subscriber endpoints
  USER_INFO: '{version}/subscribers/me',
  USER_REGISTRATION: '{version}/subscribers/registrations',
  BOOKING_LIST: '{version}/bookings',
  PREFILL_REGISTRATION_DATA: '{version}/subscriptions/my-external-registration-data',

  // Plan endpoints
  PLAN_LIST: '{version}/plans',
  ADDON_LIST: '{version}/plans/{plan_code}/addons',

  // Catalog
  // Routes endpoints
  ROUTE_LIST: '{version}/routes',
  // Currencies
  CURRENCY_LIST: '{version}/currencies',

  // Geography endpoints
  COUNTRIES: '{version}/countries',
  STATES: '{version}/countries/{country}/states',
  STATION_GROUPS: '{version}/station-groups',

  // Booking endpoints
  BOOKING_FEATURES: '{version}/subscriptions/{subscriptionId}/bookings/features',
  BOOKING_ANCILLARIES: '{version}/subscriptions/{subscriptionId}/bookings/ancillaries',
  TRIP_SEARCH: '{version}/subscriptions/{subscriptionId}/bookings/availability',
  TRIP_SEARCH_PARAMS: '{version}/subscriptions/{subscriptionId}/bookings/availability-params',
  AVAILABILITY_GROUPS: '{version}/subscriptions/{subscriptionId}/bookings/availability-params/groups',
  QUOTATION: '{version}/subscriptions/{subscriptionId}/bookings/quotation',
  BOOKING: '{version}/subscriptions/{subscriptionId}/bookings/{bookingId}',
  CREATE_BOOKING: '{version}/subscriptions/{subscriptionId}/bookings',
  BOOKING_PAYMENTS: '{version}/subscriptions/{subscriptionId}/bookings/{bookingId}/payments',
  BOOKING_PAYMENT_AUTHENTICATION_NOTIFICATIONS: '{version}/subscriptions/{subscriptionId}/bookings/{bookingId}/payments/authentications/notifications',

  // Workflow endpoints
  WORKFLOW_STATUS: '{version}/workflows/{workflowId}/status',

  // Payment endpoints
  PAYERS: '{version}/identities/me/payers',
  SUBSCRIPTIONS_PAYMENT_INTENT: '{version}/subscriptions/payments',
  MY_PAYMENTS: '{version}/payers/me/payments',
  MY_PAYMENT_METHOD: '{version}/subscribers/me/payment-methods',
  AUTHORIZE: '{version}/payments/{paymentId}/authorize',
  PAYMENT_BY_PROVIDER: '{version}/providers/{provider}/payments/{paymentId}',
  PAYMENT_BY_PROVIDER_MERCHANT: '{version}/providers/{provider}/merchants/{merchant}/payments/{paymentId}',
  CONFIRM_PAYMENT_INTENT: '{version}/providers/{provider}/payments/{paymentId}/confirm',
  PAYMENT_CONSENTS: '{version}/providers/{provider}/consents',
  PAYMENT_TOKENIZATION_INIT: '{version}/providers/{provider}/initialize',
  PAYMENT_AUTHENTICATIONS: '{version}/payments/authentications',
  PAYMENT_AUTHENTICATION: '{version}/providers/{provider}/payments/authentications/{authenticationId}',

  BILLING_INFO: '{version}/billing',
  BILLING_CALENDAR: '{version}/billing/calendar',
  BILLING_PAYMENT_RETRIES: '{version}/billing/payment-retries',
  BALANCE: '{version}/billing/balance',
  RECEIPTS: '{version}/billing/receipts',
  RECEIPT: '{version}/billing/receipts/{receiptId}',

  INVOICES: '{version}/subscriptions/{subscriptionId}/invoices', // TODO: CVO-18809 replace with outstanding balance endpoint
  CAMPAIGN_LIST: '{version}/campaigns',
} as const;

/**
 * Type-safe endpoint values
 */
export type BffEndpoint = typeof BFF_ENDPOINTS[keyof typeof BFF_ENDPOINTS];
